import {FC, useContext, useEffect} from 'react';
import {useRecoilStateLoadable} from 'recoil';
import {Controller, useForm} from 'react-hook-form';

import Modal from '@mui/material/Modal/Modal';
import Box from '@mui/system/Box';
import Stack from '@mui/system/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import ShowContactPersonsContainer from './ShowContactPersonsContainer';
import ControlledInput from '../../../../ui/controlled-input/ControlledInput';

import {ICompanyFormInputs, ICompany} from '../../../../shared/types/company.types';

import {contactPersonsOfCompanyQuery} from '../../../../store/contact-person/selectors';
import {EventBusContext} from '../../../../providers/EventBus';
import {Autocomplete, Button, TextField} from '@mui/material';
import {useSearchCompanies} from '../../../../hooks/useSearchCompanies';

interface IShowCompany {
  open: boolean;
  company: ICompany;
  handleClose: () => void;
  isEditable?: boolean;
  onChange?: (company: ICompany) => void;
}

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  p: '16px 16px 48px 16px',
};

const ShowCompany: FC<IShowCompany> = ({open, handleClose, company, isEditable = false, onChange}) => {
  const [{state, contents}, setContactPersons] = useRecoilStateLoadable(contactPersonsOfCompanyQuery);
  const {handleSearch, data, searchTerm, searchLoading} = useSearchCompanies();
  const {bus} = useContext(EventBusContext);

  const {
    control,
    reset,
    watch,
    setValue,
    getValues,
    formState: {isDirty},
  } = useForm<ICompanyFormInputs>({
    defaultValues: {
      fullCompanyName: company.fullCompanyName,
      inn: company.inn,
      ogrn: company.ogrn,
      website: company.website,
      description: company.description,
      company,
    },
  });

  const watched = watch();

  const isSaveButtonDisabled = !watched.company || !isDirty;

  useEffect(() => {
    state === 'hasValue' && setContactPersons(contents);

    state === 'hasError' && bus?.emit('error', [contents]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, contents]);

  useEffect(() => {
    return () => reset();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  return state === 'loading' ? (
    <Backdrop sx={{zIndex: (theme) => theme.zIndex.drawer + 1}} open={open} onClick={handleClose}>
      <CircularProgress color="primary" />
    </Backdrop>
  ) : (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <Toolbar
          sx={{
            pl: {sm: 1},
            pr: {xs: 1, sm: 1},
            mb: 1,
          }}
        >
          <Typography sx={{flex: '1 1 100%'}} variant="h6" id="formTitle" component="div">
            {company.fullCompanyName}
          </Typography>
        </Toolbar>
        <Stack direction="column" spacing={2} sx={{mb: isEditable ? 3 : undefined}}>
          {!isEditable ? (
            <ControlledInput control={control} name="fullCompanyName" disabled label="Полное название компании" />
          ) : (
            <Controller
              control={control}
              rules={{
                required: 'Обязательно для заполнения',
              }}
              name="company"
              render={({field: {value}, fieldState: {error}}) => (
                <Autocomplete
                  fullWidth
                  onChange={(_, newValue) => {
                    if (newValue) {
                      const company = newValue as ICompany;

                      setValue('company', company, {shouldDirty: true});
                      setValue('fullCompanyName', company.fullCompanyName, {shouldDirty: true});
                      setValue('inn', company.inn, {shouldDirty: true});
                      setValue('ogrn', company.ogrn, {shouldDirty: true});
                      setValue('website', company.website, {shouldDirty: true});
                      setValue('description', company.description, {shouldDirty: true});
                    } else {
                      setValue('company', null, {shouldDirty: true});
                    }
                  }}
                  value={value}
                  getOptionLabel={(option) => `${option.fullCompanyName},   ИНН: ${option.inn}`}
                  inputValue={searchTerm}
                  onInputChange={(_, newInputValue) => {
                    handleSearch(newInputValue);
                  }}
                  options={data}
                  loading={searchLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Выбор компании"
                      placeholder="Название / ИНН / комментарий"
                      error={!!error}
                      helperText={error ? error.message : ''}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          )}
          <Stack direction="row" spacing={2}>
            <ControlledInput
              key={watched.inn || 'inn'}
              control={control}
              name="inn"
              disabled
              label="ИНН"
              size="small"
            />
            <ControlledInput
              key={watched.ogrn || 'ogrn'}
              control={control}
              name="ogrn"
              disabled
              label="ОГРН"
              size="small"
            />
            <ControlledInput
              control={control}
              key={watched.website || 'website'}
              name="website"
              label="Сайт компании"
              size="small"
              sx={{flexGrow: 2}}
              disabled
            />
          </Stack>
          <ControlledInput
            key={watched.description || 'description'}
            control={control}
            name="description"
            label="Комментарий"
            disabled
            multiline
          />
          {!!contents.length && <ShowContactPersonsContainer contactPersons={contents} />}
        </Stack>
        {isEditable && onChange && (
          <Stack direction="row" sx={{width: '100%', justifyContent: 'flex-end'}}>
            <Button
              onClick={() => {
                if (onChange) {
                  const data = getValues();

                  if (!data.company) return;

                  onChange(data.company);
                  handleClose();
                }
              }}
              disabled={isSaveButtonDisabled}
            >
              Сохранить
            </Button>
          </Stack>
        )}
      </Box>
    </Modal>
  );
};

export default ShowCompany;
